// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.
import {
    ISoftwareVersion,
    ISubscription,
    StripeProductTier,
    SubscriptionStatus
} from 'src/types/subscription';

export function isHardwareSub(subscription: ISubscription): boolean {
    return subscription && subscription.stripe_product &&
      (subscription.stripe_product.tier === StripeProductTier.HARDWARE ||
        subscription.stripe_product.tier === StripeProductTier.HARDWARE2);
}

export function isFullDriveEncryptionSub(subscription: ISubscription): boolean {
    return subscription && subscription.stripe_product &&
      subscription.stripe_product.tier === StripeProductTier.FDE;
}

export function isSubscriptionExpired(subscription: ISubscription): boolean {
    const subscription_date = new Date(subscription.exp_timestamp);
    const subscription_date_utc = Date.UTC(subscription_date.getFullYear(),
        subscription_date.getMonth(), subscription_date.getDate(),
        subscription_date.getHours(), subscription_date.getMinutes(),
        subscription_date.getSeconds());
    const date = new Date();
    const now_utc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(),
        date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());

    const expired = now_utc > subscription_date_utc;
    return expired || subscription.status === SubscriptionStatus.SUBSCRIPTION_STATUS_EXPIRED;
}

export function isValidSubscription(subscription: ISubscription): boolean {
    const expired = new Date() > new Date(subscription.exp_timestamp);
    return !expired && subscription.status === SubscriptionStatus.SUBSCRIPTION_STATUS_VALID;
}

export function getFirstValidSubscription(subscriptions: ISubscription[]): ISubscription {
    // Note that we are treating fully used but not expired subscriptions as valid to return, if that's all that's available
    // somewhat wasteful to fully sort on quantity, and then filter
    // Did not include hardware, hardware raid, or FDE since we don't use activation codes for them.
    const validSubscriptions: ISubscription[] = subscriptions.filter(value => isValidSubscription(value))
        .sort((a, b) => a.used - a.count > b.used - b.count ? 1 : -1);
    // Try for Plus/Business
    let sub = validSubscriptions.find(s => s && s.stripe_product && s.stripe_product.tier === StripeProductTier.BUSINESS);
    if (!sub) {
        sub = validSubscriptions.find(s => s && s.stripe_product && s.stripe_product.tier === StripeProductTier.PERSONAL);
    }
    if (!sub) {
        sub = validSubscriptions.find(s => s && s.stripe_product && s.stripe_product.tier === StripeProductTier.BITLOCKER);
    }
    if (!sub) {
        sub = validSubscriptions.find(s => s && s.stripe_product && s.stripe_product.tier === StripeProductTier.FREE);
    }
    return sub;
}

export function get_uuid_filename(softwareVersion: ISoftwareVersion, system: string, uuid: string): string {
    let subscription_filename = null;
    if (softwareVersion) {
      if (softwareVersion.subscription_path) {
        subscription_filename = softwareVersion.subscription_path;
      } else {
        if ( !system ) {
            system = softwareVersion.system;
        }
        if (softwareVersion.path && uuid && system) {
          const installerName = softwareVersion.path.trim().split('/').pop();
          const installerType = installerName.split('.').pop();
          const installerBody = softwareVersion.path.substr(0, softwareVersion.path.lastIndexOf('.' + installerType));
          subscription_filename = installerBody + '_' + softwareVersion.feature_flags + '_' + uuid + '_' + system + '.' + installerType;
        }
      }
    }
    return subscription_filename;
}

export function sortSubscriptionsByDate(a: ISubscription, b: ISubscription): -1 | 0 | 1 {
    const aExp = new Date(a.exp_timestamp);
    const bExp = new Date(b.exp_timestamp);
    if (aExp == bExp) {
      return 0;
    }
    return aExp < bExp ? 1 : -1;
}

export function sortSubscriptionsByPriority(a: ISubscription, b: ISubscription): -1 | 0 | 1 {
    const priorityOrder =
      [
        StripeProductTier.BUSINESS,
        StripeProductTier.BITLOCKER,
        StripeProductTier.AGENT,
        StripeProductTier.HARDWARE2,
        StripeProductTier.HARDWARE,
        StripeProductTier.FDE,
        StripeProductTier.PERSONAL,
        StripeProductTier.FREE,
    ];
    const aIndex = priorityOrder.findIndex(t => t === a.stripe_product.tier);
    const bIndex = priorityOrder.findIndex(t => t === b.stripe_product.tier);
    if (aIndex === -1 && bIndex === -1) {
      return 0;
    }
    if (aIndex === -1) {
      return 1;
    }
    if (bIndex === -1) {
      return -1;
    }
    if (aIndex === bIndex) {
      const remainingA = a.count - a.used;
      const remainingB = b.count - b.used;
        if (remainingA > remainingB) {
          return -1;
        }
        if (remainingA < remainingB) {
          return 1;
        }
        return sortSubscriptionsByDate(a, b);
    }

    return aIndex < bIndex ? -1 : 1;
}
